import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import TurnstileWidget from '../components/TurnstileWidget';
import { Loader2, School, Mail, Lock, User, Phone, CheckCircle, XCircle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Signup = () => {
  const [formData, setFormData] = useState({
    schoolName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  
  // Real-time subdomain validation
  const [subdomainPreview, setSubdomainPreview] = useState('');
  const [subdomainStatus, setSubdomainStatus] = useState('idle'); // idle, checking, available, taken
  const [subdomainError, setSubdomainError] = useState('');

  const generateSubdomain = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 20);
  };

  // Debounced subdomain check
  const checkSubdomain = useCallback(async (subdomain) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainStatus('idle');
      setSubdomainError('');
      return;
    }

    setSubdomainStatus('checking');
    
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('subdomain')
        .eq('subdomain', subdomain)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSubdomainStatus('taken');
        setSubdomainError(`"${subdomain}" is already taken. Please choose a different school name.`);
      } else {
        setSubdomainStatus('available');
        setSubdomainError('');
      }
    } catch (err) {
      console.error('Error checking subdomain:', err);
      setSubdomainStatus('idle');
    }
  }, []);

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (subdomainPreview) {
        checkSubdomain(subdomainPreview);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [subdomainPreview, checkSubdomain]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSchoolNameChange = (e) => {
    const name = e.target.value;
    handleChange(e);
    const subdomain = generateSubdomain(name);
    setSubdomainPreview(subdomain);
    
    // Reset status while typing
    if (subdomain.length < 3) {
      setSubdomainStatus('idle');
      setSubdomainError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validation
    if (subdomainStatus === 'taken') {
      setError('Please choose a different school name. The portal URL is already taken.');
      setIsLoading(false);
      return;
    }

    if (subdomainStatus === 'checking') {
      setError('Please wait while we check if your portal name is available...');
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setIsLoading(false);
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the security check below.');
      setIsLoading(false);
      return;
    }

    try {
      // Single API call to create both tenant and admin user
      const response = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          schoolName: formData.schoolName,
          email: formData.email.toLowerCase(),
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          turnstileToken
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create account');
      }

      setSuccess(true);
      
      // Redirect to their new portal using PATH-BASED routing
      // NEW: admin.classroomdragon.com/:subdomain/dashboard
      setTimeout(() => {
        window.location.href = `https://admin.classroomdragon.com/${data.tenant.subdomain}/dashboard`;
      }, 2000);

    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-fuchsia-50 to-purple-100 p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={40} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Success!</h2>
          <p className="text-gray-600 mb-4">
            Your school account has been created.
          </p>
          <p className="text-purple-600 font-medium">
            Redirecting to your portal...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-fuchsia-50 to-purple-100 p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4">
            <img 
              src={`${process.env.PUBLIC_URL}/favicon.svg`} 
              alt="ClassroomDragon" 
              className="w-full h-full object-contain drop-shadow-lg"
            />
          </div>
          <h1 className="text-3xl font-bold text-purple-900 mb-2">Start Your School's Free Trial</h1>
          <p className="text-purple-600">Set up your classrooms in minutes to start testing all the features we offer</p>
        </div>

        {/* Signup Form */}
        <div className="bg-white rounded-2xl shadow-xl shadow-purple-100 p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* School Name */}
            <div>
              <label className="form-label">School/Organization Name</label>
              <div className="relative">
                <School className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  name="schoolName"
                  value={formData.schoolName}
                  onChange={handleSchoolNameChange}
                  className="form-input pl-12"
                  placeholder="e.g., Sunshine Dance Academy"
                  required
                />
              </div>
              {subdomainPreview && (
                <div className="mt-2">
                  <p className="text-xs text-purple-600">
                    Your portal: <strong>admin.classroomdragon.com/{subdomainPreview}</strong>
                    {subdomainStatus === 'checking' && (
                      <span className="ml-2 inline-flex items-center text-gray-500">
                        <Loader2 size={12} className="animate-spin mr-1" /> Checking...
                      </span>
                    )}
                    {subdomainStatus === 'available' && (
                      <span className="ml-2 inline-flex items-center text-green-600">
                        <CheckCircle2 size={12} className="mr-1" /> Available
                      </span>
                    )}
                    {subdomainStatus === 'taken' && (
                      <span className="ml-2 inline-flex items-center text-red-600">
                        <XCircle size={12} className="mr-1" /> Taken
                      </span>
                    )}
                  </p>
                  {subdomainError && (
                    <p className="text-xs text-red-600 mt-1">{subdomainError}</p>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Double check your spelling, this will be your custom login URL.
              </p>
            </div>

            {/* Name Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">First Name</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="form-input pl-12"
                    placeholder="First"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Last"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="form-label">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input pl-12"
                  placeholder="you@school.com"
                  required
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="form-label">Phone (optional)</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input pl-12"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input pl-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">At least 8 characters</p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="form-label">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="form-input"
                placeholder="••••••••"
                required
              />
            </div>

            {/* Cloudflare Turnstile spam protection */}
            <TurnstileWidget onVerify={setTurnstileToken} />

            <button
              type="submit"
              disabled={isLoading || !turnstileToken}
              className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-70"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  <span>Creating your account...</span>
                </>
              ) : (
                <span>Start Free Trial</span>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-purple-600 hover:text-purple-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500 mb-2">Free 14-day trial • No credit card required</p>
          <div className="flex items-center justify-center space-x-4 text-xs text-gray-400">
            <span>🔒 SSL Secure</span>
            <span>✓ SOC 2 Compliant</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
